import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const themes = [
  {
    id: "grief",
    title: "Processing grief and loss",
    text: "Even when ending it was your choice, losing a relationship is a real loss — of routines, shared friends and the future you had pictured. Counseling gives that grief a place to go, so it doesn't follow you into everything that comes next.",
  },
  {
    id: "identity",
    title: "Rebuilding your sense of self",
    text: "After years of being part of a 'we,' answering to 'I' again can feel strange. We work on rediscovering who you are on your own — your values, your rhythms, your confidence — not as a reaction to the relationship, but on your own terms.",
  },
  {
    id: "patterns",
    title: "Making sense of what happened",
    text: "Understanding what actually happened — the dynamics, the patterns, the part each of you played — isn't about assigning blame. It's how you make sure the next chapter of your life doesn't quietly repeat the last one.",
  },
  {
    id: "coparenting",
    title: "Co-parenting as ex-partners",
    text: "The romance may be over, but if you share children, the parenting relationship continues. We build the skills that keep kids out of the middle: brief, factual communication, clear boundaries, and two homes that feel calm and predictable.",
  },
  {
    id: "boundaries",
    title: "Setting boundaries with an ex",
    text: "Contact, space, family gatherings, new relationships — life after a breakup needs new rules. We define boundaries that protect your peace and practice holding them without guilt or escalation.",
  },
  {
    id: "trust",
    title: "Trusting and dating again",
    text: "At some point the question shifts from 'what went wrong' to 'could I ever do this again?' We move at your pace — rebuilding trust in your own judgment first, and openness to someone new only when you're ready.",
  },
];

const PostRelationship = () => {
  usePageMeta(
    "Post-Relationship Counseling in California | Holistic Healing Psychotherapy",
    "Compassionate counseling for life after a breakup or divorce: processing grief, rebuilding your sense of self, co-parenting as ex-partners and learning to trust again. California LCSW, telehealth statewide."
  );

  return (
    <div data-testid="postrel-page">
      <section className="bg-forest-950 pt-16 pb-20 lg:pt-28 lg:pb-28 relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold-500/8 blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="postrel-eyebrow">Post-Relationship Counseling</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-cream-50" data-testid="postrel-headline">
              Every ending begins a different relationship.{" "}
              <em className="text-gold-400">Let's write yours together.</em>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl" data-testid="postrel-intro">
              Whether the ending was your choice, your partner's, or mutual, the months after a relationship ends
              can be disorienting. The grief is real, the questions are loud, and everyone seems to expect you to be
              "over it" on a schedule.
            </p>
            <p className="mt-4 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl">
              Post-relationship counseling is a structured, nonjudgmental place to process what happened, rebuild
              who you are outside the relationship, and move forward with clarity — not just time.
            </p>
            <Link
              to="/contact"
              data-testid="postrel-schedule-cta"
              className="inline-flex items-center gap-2 mt-9 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-white"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="What We Work On" title="Healing that's aimed at your actual life." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {themes.map((t, i) => (
              <FadeIn key={t.id} delay={i * 0.08}>
                <div className="group py-8 border-t border-forest-800/10 hover:border-gold-500/50 transition-colors duration-500" data-testid={`postrel-theme-${t.id}`}>
                  <div className="flex gap-6">
                    <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500 group-hover:bg-gold-400 transition-colors duration-500 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-2xl text-forest-800 mb-3">{t.title}</h3>
                      <p className="text-base text-sage leading-relaxed">{t.text}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-forest-800 leading-snug" data-testid="postrel-close">
              The end of a relationship is not a verdict on you. It's a loss that deserves real care — and a chance
              to build what comes next <em className="text-mauve-500">on purpose</em>.
            </p>
            <p className="mt-6 text-sm text-sage max-w-xl mx-auto leading-relaxed" data-testid="postrel-note">
              Still deciding whether to stay or separate?{" "}
              <Link to="/separation-counseling" className="text-gold-700 underline underline-offset-4 hover:text-gold-600" data-testid="postrel-separation-link">Separation & post-marital counseling</Link>{" "}
              fits that stage. For broader one-on-one support, see{" "}
              <Link to="/individual-therapy" className="text-gold-700 underline underline-offset-4 hover:text-gold-600" data-testid="postrel-individual-link">individual therapy</Link>.
            </p>
            <Link
              to="/contact"
              data-testid="postrel-bottom-cta"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="You don't have to do the 'after' alone."
        copy="Schedule a free 20-minute consultation to talk about where you are and what support could look like."
      />
    </div>
  );
};

export default PostRelationship;
